import React from 'react';
import { Box, Chip, Divider, Typography } from '@mui/material';
import {
  BidRecommendation,
  Action,
  Confidence,
  SupportingMetrics,
} from '../../types/bidRecommendations';
import MetricsCharts from './MetricsCharts';

interface RecommendationDetailProps {
  rec: BidRecommendation;
}

const actionLabels: Record<Action, string> = {
  increase: 'Increase',
  decrease: 'Decrease',
  hold: 'Hold',
  manual_review: 'Manual Review',
};

const confidenceColors: Record<Confidence, 'success' | 'warning' | 'error'> = {
  high: 'success',
  medium: 'warning',
  low: 'error',
};

const metricLabels: Record<string, string> = {
  cost_per_lead: 'Cost / Lead',
  agp_over_cost: 'AGP / Cost',
  agp_value: 'AGP Value',
  lead_trend_pct: 'Lead Trend %',
  total_leads: 'Total Leads',
  dih_rate: 'DIH Rate',
  ctr: 'CTR',
  topword_pct: 'TopWord %',
  buying_intent_pct: 'Buying Intent %',
  nowordgroup_pct: 'NoWordGroup %',
  cpa_30d: 'CPA (30d)',
  cpa_7d_vs_30d_pct: 'CPA 7d vs 30d %',
  cpa_volatility_cv: 'CPA Volatility CV',
  roas_7d_vs_30d_pct: 'ROAS 7d vs 30d %',
  spend_30d: 'Spend (30d)',
  mobile_cost_pct: 'Mobile Cost %',
  platform_conversions: 'Platform Conversions',
  attribution_leads: 'Attribution Leads',
};

function formatMetricValue(key: string, val: number): string {
  const currencyKeys = ['cost_per_lead', 'agp_value', 'cpa_30d', 'spend_30d'];
  const pctKeys = [
    'lead_trend_pct',
    'dih_rate',
    'ctr',
    'topword_pct',
    'buying_intent_pct',
    'nowordgroup_pct',
    'cpa_7d_vs_30d_pct',
    'roas_7d_vs_30d_pct',
    'mobile_cost_pct',
  ];

  if (currencyKeys.includes(key))
    return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (pctKeys.includes(key)) return `${val.toFixed(1)}%`;
  if (Number.isInteger(val)) return val.toLocaleString();
  return val.toFixed(2);
}

const RecommendationDetail: React.FC<RecommendationDetailProps> = ({ rec }) => {
  const metrics = rec.supporting_metrics as SupportingMetrics;
  const metricEntries = Object.entries(metrics).filter(
    ([, v]) => v != null
  ) as [string, number][];

  return (
    <Box px={3} py={2}>
      <Typography variant="h6" gutterBottom>
        {rec.ad_group_name}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {rec.campaign_name} &middot;{' '}
        {rec.platform === 'google' ? 'Google Ads' : 'Microsoft Ads'} &middot;{' '}
        {rec.match_type}
      </Typography>

      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Chip label={actionLabels[rec.action]} size="small" />
        <Chip
          label={rec.confidence}
          color={confidenceColors[rec.confidence]}
          size="small"
          variant="outlined"
        />
        {rec.risk_flags.map((f) => (
          <Chip
            key={f}
            label={f.replace(/_/g, ' ')}
            size="small"
            color="warning"
            variant="outlined"
          />
        ))}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Bid info */}
      <Box display="flex" gap={3} mb={2}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Current Bid
          </Typography>
          <Typography variant="h6">${rec.current_bid.toFixed(2)}</Typography>
        </Box>
        {rec.estimated_new_bid != null && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Est. New Bid
            </Typography>
            <Typography variant="h6">
              ${rec.estimated_new_bid.toFixed(2)}
            </Typography>
          </Box>
        )}
        {rec.bid_change_pct != null && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Change
            </Typography>
            <Typography
              variant="h6"
              color={rec.bid_change_pct > 0 ? 'success.main' : 'error.main'}
            >
              {rec.bid_change_pct > 0 ? '+' : ''}
              {rec.bid_change_pct}%
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Primary reason */}
      <Typography variant="subtitle2" gutterBottom>
        Primary Reason
      </Typography>
      <Typography variant="body2" mb={2}>
        {rec.primary_reason}
      </Typography>

      {/* Narrative */}
      <Typography variant="subtitle2" gutterBottom>
        Narrative
      </Typography>
      <Typography variant="body2" mb={2} sx={{ whiteSpace: 'pre-wrap' }}>
        {rec.narrative}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {/* Supporting metrics */}
      {metricEntries.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Supporting Metrics
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={2}>
            {metricEntries.map(([key, val]) => (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary">
                  {metricLabels[key] || key}
                </Typography>
                <Typography variant="body2">
                  {formatMetricValue(key, val)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Review status */}
      {rec.review_status !== 'pending' && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Review
          </Typography>
          <Box display="flex" gap={1} alignItems="center" mb={1}>
            <Chip
              label={rec.review_status}
              color={rec.review_status === 'accepted' ? 'success' : 'error'}
              size="small"
            />
            {rec.published_at && (
              <Chip
                label="Published"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
          {rec.review_reason && (
            <Typography variant="body2" color="text.secondary">
              {rec.review_reason}
            </Typography>
          )}
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <MetricsCharts adGroupId={rec.ad_group_id} platform={rec.platform} />
    </Box>
  );
};

export default RecommendationDetail;
